/**
 * Сменное задание прорабу (ПЛ-28): работа графика на день смены с объёмом на этот день — из недельно-суточного
 * плана. Прораб видит его в «Мой день» как пакет дня; принятый отчёт смены по нему меняет факт графика.
 */
import { z } from 'zod';

export const SHIFT_METHODS = {
  even: 'Равномерно по рабочим дням',
  curve: 'По ресурсной кривой',
  manual: 'Вручную',
} as const;
export type ShiftMethod = keyof typeof SHIFT_METHODS;
export const SHIFT_TASK_STATUSES = { issued: 'Выдано', cancelled: 'Отменено' } as const;

const DayStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Объём — десятичной строкой: до 6 знаков после запятой, как в принятом факте площадки */
export const ShiftQuantity = z
  .string()
  .trim()
  .regex(/^\d{1,15}(\.\d{1,6})?$/, 'Объём — число, до 6 знаков после точки');

export const ShiftTaskRow = z.object({
  id: z.string(),
  scheduleId: z.string(),
  projectId: z.string(),
  activityId: z.string(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  day: z.string(),
  foremanId: z.string(),
  foremanName: z.string().nullable(),
  quantity: z.string(),
  method: z.enum(['even', 'curve', 'manual']),
  packageId: z.string(),
  commitmentId: z.string().nullable(),
  status: z.enum(['issued', 'cancelled']),
  note: z.string().nullable(),
  exceptionReason: z.string().nullable(),
  issuedByName: z.string().nullable(),
  issuedAt: z.string().nullable(),
  /** Принятый объём по отчётам смены этого пакета за день задания */
  fact: z.string(),
  /** По пакету дня уже есть отчёт смены (в любом состоянии) */
  reported: z.boolean(),
  canCancel: z.boolean(),
});
export type ShiftTaskRow = z.infer<typeof ShiftTaskRow>;

export const ShiftTaskList = z.object({
  from: z.string(),
  to: z.string(),
  tasks: z.array(ShiftTaskRow),
  /** Может выдавать задания: правит график */
  canIssue: z.boolean(),
  /** Видны только свои задания (прораб), а не все задания проекта */
  mineOnly: z.boolean(),
});
export type ShiftTaskList = z.infer<typeof ShiftTaskList>;

export const ShiftTaskInput = z.object({
  day: DayStr,
  foremanId: z.uuid(),
  items: z
    .array(
      z.object({
        activityId: z.uuid(),
        /** Пусто — посчитать способом method */
        quantity: ShiftQuantity.optional(),
        method: z.enum(['even', 'curve']).optional(),
      }),
    )
    .min(1)
    .max(50),
  note: z.string().trim().max(500).optional(),
  exceptionReason: z.string().trim().min(3).max(500).optional(),
});
export type ShiftTaskInput = z.infer<typeof ShiftTaskInput>;

/** Подсказка объёма на день обоими способами */
export const ShiftQuantityHint = z.object({
  activityId: z.string(),
  day: z.string(),
  unit: z.string().nullable(),
  /** Рабочий ли день по календарю работы */
  working: z.boolean(),
  remaining: z.string().nullable(),
  even: z.string().nullable(),
  curve: z.string().nullable(),
  /** Какая кривая взята: по назначению с наибольшей трудоёмкостью */
  curveName: z.string(),
  /** Почему объём не посчитать */
  problem: z.string().nullable(),
});
export type ShiftQuantityHint = z.infer<typeof ShiftQuantityHint>;
